import Phaser from 'phaser';
import { STATIONS, STATION_ORDER } from './content';
import type { GameStore } from './GameStore';
import type { GameState, StationId } from './types';

const WIDTH = 960;
const HEIGHT = 540;
const MIN_ESTATE_ART_TIER = 1;
const MAX_ESTATE_ART_TIER = 5;

type StationZone = { x: number; y: number; w: number; h: number };

const ZONES: Record<StationId, StationZone> = {
  vineyard: { x: 184, y: 128, w: 352, h: 210 },
  crush: { x: 520, y: 132, w: 300, h: 182 },
  cellar: { x: 510, y: 278, w: 268, h: 176 },
  bottling: { x: 775, y: 264, w: 260, h: 178 },
  sales: { x: 176, y: 360, w: 292, h: 188 }
};

function estateTextureKey(tier: number): string {
  return `estate-tier-${tier}`;
}

function estateArtTier(state: GameState): number {
  return Phaser.Math.Clamp(Math.floor(state.estateTier), MIN_ESTATE_ART_TIER, MAX_ESTATE_ART_TIER);
}

class EstateScene extends Phaser.Scene {
  private readonly store: GameStore;
  private readonly onSelect: (stationId: StationId) => void;
  private unsubscribe: (() => void) | null = null;
  private readonly hoverGuides = new Map<StationId, Phaser.GameObjects.Graphics>();
  private readonly hoverLabels = new Map<StationId, Phaser.GameObjects.Text>();
  private background!: Phaser.GameObjects.Image;
  private backgroundTier = MIN_ESTATE_ART_TIER;
  private tintOverlay!: Phaser.GameObjects.Rectangle;

  constructor(store: GameStore, onSelect: (stationId: StationId) => void) {
    super('estate');
    this.store = store;
    this.onSelect = onSelect;
  }

  preload(): void {
    for (let tier = MIN_ESTATE_ART_TIER; tier <= MAX_ESTATE_ART_TIER; tier += 1) {
      this.load.image(estateTextureKey(tier), `./assets/estate-tier-${tier}.png`);
    }
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#18100b');
    this.backgroundTier = estateArtTier(this.store.getState());
    this.background = this.add
      .image(WIDTH / 2, HEIGHT / 2, estateTextureKey(this.backgroundTier))
      .setDisplaySize(WIDTH, HEIGHT);
    this.tintOverlay = this.add.rectangle(WIDTH / 2, HEIGHT / 2, WIDTH, HEIGHT, 0x8c2f6a, 0)
      .setDepth(75)
      .setBlendMode(Phaser.BlendModes.MULTIPLY);
    this.createStationHotspots();
    this.unsubscribe = this.store.subscribe((state) => this.sync(state));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unsubscribe?.());
  }

  private createStationHotspots(): void {
    for (const stationId of STATION_ORDER) {
      const zone = ZONES[stationId];
      const left = Math.round(zone.x - zone.w / 2);
      const top = Math.round(zone.y - zone.h / 2);
      const right = left + zone.w;
      const bottom = top + zone.h;
      const guide = this.add.graphics().setDepth(90).setVisible(false);
      guide.fillStyle(0x140d09, 0.13).fillRoundedRect(left, top, zone.w, zone.h, 8);
      guide.fillStyle(0xffda7a, 0.95)
        .fillRect(left, top, 20, 2).fillRect(left, top, 2, 20)
        .fillRect(right - 20, top, 20, 2).fillRect(right - 2, top, 2, 20)
        .fillRect(left, bottom - 2, 20, 2).fillRect(left, bottom - 20, 2, 20)
        .fillRect(right - 20, bottom - 2, 20, 2).fillRect(right - 2, bottom - 20, 2, 20);
      this.hoverGuides.set(stationId, guide);

      const label = this.add.text(zone.x, Math.max(16, top + 12), STATIONS[stationId].label.toUpperCase(), {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#ffedb5',
        backgroundColor: '#24150dee',
        padding: { x: 7, y: 4 },
        stroke: '#130b08',
        strokeThickness: 2
      }).setOrigin(0.5).setDepth(91).setVisible(false);
      this.hoverLabels.set(stationId, label);

      const interactionZone = this.add.zone(zone.x, zone.y, zone.w, zone.h)
        .setDepth(100)
        .setInteractive({ useHandCursor: true });
      interactionZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        // Phaser also observes pointer events that originate in DOM overlays above its canvas.
        // Only direct canvas clicks may select an estate hotspot; otherwise a manager/upgrade
        // click can select the station beneath the panel before the button's click fires.
        if (pointer.event?.target !== this.game.canvas) return;
        this.onSelect(stationId);
      });
      interactionZone.on('pointerover', () => {
        guide.setVisible(true);
        label.setVisible(true);
      });
      interactionZone.on('pointerout', () => {
        guide.setVisible(false);
        label.setVisible(false);
      });
    }
  }

  private sync(state: GameState): void {
    const nextBackgroundTier = estateArtTier(state);
    if (nextBackgroundTier !== this.backgroundTier) {
      this.backgroundTier = nextBackgroundTier;
      this.background.setTexture(estateTextureKey(nextBackgroundTier));
    }

    for (const stationId of STATION_ORDER) {
      const station = state.stations[stationId];
      this.hoverLabels.get(stationId)?.setText(
        `${STATIONS[stationId].label.toUpperCase()} · LV ${station.level} · STORAGE ${station.capacityLevel}`
      );
    }
    const evening = state.weather.includes('evening');
    this.tintOverlay.setFillStyle(evening ? 0x352058 : 0xcc7b3b, evening ? 0.12 : 0.025);
  }
}

export class EstateRenderer {
  private game: Phaser.Game;

  constructor(parent: HTMLElement, store: GameStore, onSelect: (stationId: StationId) => void) {
    this.game = new Phaser.Game({
      type: Phaser.AUTO,
      parent,
      width: WIDTH,
      height: HEIGHT,
      backgroundColor: '#18100b',
      pixelArt: true,
      antialias: false,
      roundPixels: true,
      transparent: false,
      render: { pixelArt: true, antialias: false, roundPixels: true },
      scale: { mode: Phaser.Scale.FIT, autoCenter: Phaser.Scale.CENTER_BOTH, width: WIDTH, height: HEIGHT },
      scene: [new EstateScene(store, onSelect)]
    });
  }

  destroy(): void {
    this.game.destroy(true);
  }
}
